import React, { useState, useEffect } from 'react';
import { MapPin, Settings, Save, RefreshCw, Heart, Users } from 'lucide-react';

const MatchingPreferences = () => {
  const [preferences, setPreferences] = useState({
    // Location preferences
    enable_location_matching: false,
    preferred_distance_miles: 25,
    location: '',
    latitude: null,
    longitude: null,
    // Gender preferences
    gender: '',
    interested_in: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '/api';

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/matching-preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    setGettingLocation(true);
    
    if (!navigator.geolocation) {
      setMessage('Geolocation is not supported by this browser.');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Reverse geocoding to get city/state
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (response.ok) {
            const data = await response.json();
            const location = `${data.city}, ${data.principalSubdivision}`;
            
            setPreferences(prev => ({
              ...prev,
              location,
              latitude,
              longitude
            }));
            
            setMessage('Location detected successfully!');
          }
        } catch (error) {
          // Fallback: just use coordinates
          setPreferences(prev => ({
            ...prev,
            latitude,
            longitude,
            location: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`
          }));
          setMessage('Location coordinates saved!');
        }
        
        setGettingLocation(false);
      },
      (error) => {
        setMessage('Unable to get your location. Please enter it manually.');
        setGettingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleGenderInterestChange = (gender, checked) => {
    setPreferences(prev => ({
      ...prev,
      interested_in: checked
        ? [...prev.interested_in, gender]
        : prev.interested_in.filter(g => g !== gender)
    }));
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${backendUrl}/matching-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        setMessage('Matching preferences saved successfully!');
      } else {
        setMessage('Error saving preferences. Please try again.');
      }
    } catch (error) {
      setMessage('Error saving preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
            <Heart className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Matching Preferences</h1>
            <p className="text-gray-600">Control who you see and how you're matched</p>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.includes('Error') || message.includes('Unable')
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        {/* Gender Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Gender Preferences</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your gender
              </label>
              <select
                value={preferences.gender || ''}
                onChange={(e) => setPreferences(prev => ({ ...prev, gender: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              >
                <option value="">Select your gender</option>
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I'm interested in dating
              </label>
              <div className="grid grid-cols-2 gap-3">
                {genderOptions.map(option => (
                  <label key={option.value} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.interested_in?.includes(option.value) || false}
                      onChange={(e) => handleGenderInterestChange(option.value, e.target.checked)}
                      className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Select all genders you're interested in dating. This helps us show you relevant matches.
              </p>
            </div>
          </div>
        </div>

        {/* Location Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <MapPin className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Location Preferences</h2>
          </div>

          {/* Enable Location Matching Toggle */}
          <div className="mb-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Location-Based Matching</h3>
                <p className="text-sm text-gray-600">Show me matches based on distance</p>
              </div>
              <button
                onClick={() => setPreferences(prev => ({
                  ...prev,
                  enable_location_matching: !prev.enable_location_matching
                }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.enable_location_matching ? 'bg-rose-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.enable_location_matching ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Location Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Location
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={preferences.location || ''}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  location: e.target.value
                }))}
                placeholder="e.g., New York, NY"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
              <button
                onClick={getCurrentLocation}
                disabled={gettingLocation}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
              >
                <RefreshCw className={`w-4 h-4 ${gettingLocation ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">
                  {gettingLocation ? 'Getting...' : 'Auto-detect'}
                </span>
              </button>
            </div>
          </div>

          {/* Distance Preference */}
          {preferences.enable_location_matching && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Distance: {preferences.preferred_distance_miles} miles
              </label>
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={preferences.preferred_distance_miles}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  preferred_distance_miles: parseInt(e.target.value)
                }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5 miles</span>
                <span>50 miles</span>
                <span>100+ miles</span>
              </div>
            </div>
          )}
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-2">
            <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How Matching Works:</p>
              <ul className="space-y-1 text-blue-700">
                <li>• <strong>Gender:</strong> Only see people who match your preferences (and vice versa)</li>
                <li>• <strong>Location:</strong> When enabled, prioritize matches within your distance range</li>
                <li>• <strong>AI Compatibility:</strong> Smart matching based on interests and personality</li>
                <li>• <strong>Privacy:</strong> Your exact location is never shared with other users</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={savePreferences}
          disabled={saving || !preferences.gender || preferences.interested_in?.length === 0}
          className="w-full bg-rose-600 text-white py-3 px-4 rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Preferences'}</span>
        </button>

        {(!preferences.gender || preferences.interested_in?.length === 0) && (
          <p className="text-xs text-gray-500 text-center mt-2">
            Please specify your gender and dating preferences to save
          </p>
        )}
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #e11d48;
          cursor: pointer;
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #e11d48;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default MatchingPreferences;